import get from 'lodash.get';
import { Equipment } from 'services/equipment/types';
import {
    VirtualizedTableItem,
    VirtualizedTableColumns
} from 'services/virtualizedTable/types';
import { selectActiveEquipment } from 'services/equipment/selectors';
// @ts-ignore
import { selectCurrentSeasonIsComplete } from 'services/currentSeasonId/selectors';
import { selectIsAdmin, selectIsManager } from 'services/usersMe/selectors';
import {
    FIELD_TYPE_STRING,
    FIELD_TYPE_NUMBER,
    FIELD_TYPE_DROPDOWN
} from 'components/VirtualizedTable/constants';
import { makeSelectTableColumnWidths } from 'services/columnWidths/selectors';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import { NozzleType } from 'services/equipmentSets/constants';

const selectEquipmentTableColumns = createDeepEqualSelector(
    [makeSelectTableColumnWidths()],
    (tableColumnWidths) => {
        const result: VirtualizedTableColumns = [
            {
                label: 'Name',
                value: 'name',
                width: 250,
                type: FIELD_TYPE_STRING,
                validators: 'isRequired'
            },
            {
                label: 'Type',
                value: 'equipmentType',
                width: 150,
                type: FIELD_TYPE_DROPDOWN,
                disableUpdating: true,
                validators: 'isRequired',
                options: [
                    {
                        key: 'NOZZLESET',
                        text: 'Nozzle Set',
                        value: 'NOZZLESET'
                    },
                    {
                        key: 'SPRAYER',
                        text: 'Sprayer',
                        value: 'SPRAYER'
                    },
                    {
                        key: 'TRACTOR',
                        text: 'Tractor',
                        value: 'TRACTOR'
                    }
                ]
            },
            {
                label: 'Reference',
                value: 'internalId',
                width: 150,
                type: FIELD_TYPE_STRING,
                emptyValueToNull: true
            },
            {
                label: 'Tank Volume (L)',
                value: 'tankVolume',
                width: 150,
                type: FIELD_TYPE_NUMBER,
                validators: [
                    'isRequiredNumber',
                    'isPositiveNumber',
                    'maxValue999999'
                ]
            },
            {
                label: 'Nozzle Type',
                value: 'nozzleType',
                width: 150,
                disableUpdating: true,
                validators: 'isRequired',
                type: FIELD_TYPE_DROPDOWN,
                options: [
                    {
                        key: NozzleType.HV,
                        text: NozzleType.HV,
                        value: NozzleType.HV
                    },
                    {
                        key: NozzleType.LV,
                        text: NozzleType.LV,
                        value: NozzleType.LV
                    },
                    {
                        key: NozzleType.WEED_BOOM,
                        text: 'Weed Boom',
                        value: NozzleType.WEED_BOOM
                    }
                ]
            }
        ];

        return result.map((column) => {
            const { width, value } = column;
            return {
                ...column,
                width: get(tableColumnWidths, value, width)
            };
        });
    }
);

const selectIsWritable = createDeepEqualSelector(
    [selectIsAdmin, selectIsManager, selectCurrentSeasonIsComplete],
    (
        isAdmin: boolean,
        isManager: boolean,
        currentSeasonIsComplete: boolean
    ): boolean => (isAdmin || isManager) && !currentSeasonIsComplete
);

const selectEquipmentItems = createDeepEqualSelector(
    selectActiveEquipment,
    (activeEquipment) =>
        activeEquipment.map(
            (equipment: Equipment): VirtualizedTableItem => ({
                key: equipment.uuid,
                id: equipment.id,
                name: equipment.name,
                equipmentType: equipment.equipmentType,
                internalId: equipment.internalId,
                tankVolume: equipment.tankVolume,
                nozzleType: equipment.nozzleType
            })
        )
);

const makeSelectEquipmentItems = (): typeof selectEquipmentItems =>
    selectEquipmentItems;

export {
    selectEquipmentTableColumns,
    selectIsWritable,
    makeSelectEquipmentItems
};
